import {useSearchResultStore} from "~/stores/searchResult.js";
import {useSearchQueryStore} from "@/stores/searchQuery.js";
import {getSearchSpaceList, getSpacesAvailability, recordSpaceImpressions} from "@/api/space";
import {useQueryFormatter} from "@/composables/useQueryFormatter";
import {AVAILABILITY_TRI_STATE_ENABLED} from "@/config/featureFlags";
import {filtersHash, priceBand, useRemarketing} from "@/composables/useRemarketing";
import {useBehaviorFlow} from "@/composables/useBehaviorFlow";

// BI P2:city_ids/category_ids 是分類 id 陣列/物件,既有 search_no_result 的
// search_city/search_category 卻是單一字串——這裡把陣列攤成逗號字串接上既有欄位,
// 不改既有事件定義(見 behavior-event-catalog.md §5 沿用既有定義)。
function toCsv(list) {
    return (list || []).length ? list.join(',') : undefined
}

// 曝光計量:批次上報一批結果的 space_id;fire-and-forget,失敗不影響瀏覽。
// 綁在「一次成功的搜尋」而非被動 watch store,避免元件 remount/re-render
// 時對 sessionStorage 持久化的舊結果重複計數(見後續 121 P1 review)。
function reportImpressions(list) {
    const spaceIds = (list || []).map((item) => item.space_id).filter(Boolean)
    if (spaceIds.length === 0) return
    recordSpaceImpressions(spaceIds).catch(() => {
    })
}

// AV P2 刀2:為一頁卡片疊上檔期三態徽章。徽章是加分訊號,批次 API 失敗就回原列表
// (不擋瀏覽)。date 為 AV P1 捕捉到的搜尋日期(YYYY/MM/DD),有帶就算當日、否則日期無關。
export async function enrichAvailability(list, date) {
    // 三態總開關關閉期間直接回原列表、不呼叫 availability API（線上預約停用，見 featureFlags）。
    if (!AVAILABILITY_TRI_STATE_ENABLED) return list || []
    const spaceIds = (list || []).map((item) => item.space_id).filter(Boolean)
    if (spaceIds.length === 0) return list || []
    try {
        const res = await getSpacesAvailability(spaceIds, date)
        const byId = {}
        ;(res.list || []).forEach((a) => {
            byId[a.space_id] = a.availability
        })
        return list.map((item) => ({...item, availability: byId[item.space_id] || ''}))
    } catch {
        return list
    }
}

export function useSearchWithStore() {
    const searchResultStore = useSearchResultStore()
    const searchQueryStore = useSearchQueryStore()
    const {setSearchQueryToRoute} = useQueryFormatter()
    const {trackSearchPerformed, trackSearchNoResult} = useRemarketing()
    const behaviorFlow = useBehaviorFlow()


    async function handleSearchWithStore(isSetSearchQueryToRoute = true) {
        const typeCategoryId = import.meta.env.VITE_APP_BASE_TYPE_CATEGORY_ID.toString()
        const queryData = searchQueryStore.getSearchQueryDate

        // AV P1(捕捉導向):日期只寫入 store/URL 供詢問預填,不參與搜尋過濾。
        // 帶 rent_time 會觸發後端 GetIdleSpaceIDs,錯誤排除詢問制場地;可預約性過濾留待 AV P2。
        // 路由/store 仍保留完整 rent_time(下方 setSearchQueryToRoute 用 queryData),僅送 API 的 payload 剝除。
        const searchPayload = {
            ...queryData,
            filter_options: {
                ...queryData.filter_options,
                rent_time: {rent_type: 0, begin_time: '', end_time: ''}
            }
        }

        // AV P2 刀2:卡片徽章沿用 AV P1 捕捉到的日期(有帶就算當日三態,否則日期無關)。
        const capturedDate = queryData.filter_options?.rent_time?.begin_time
            ? queryData.filter_options.rent_time.begin_time.split(' ')[0]
            : ''

        const recommendQueryData = {
            "pagination": {
                "dir": 1,
                "limit": 20,
                "page": 1,
                "sort": "created_date"
            },
            filter_options: {
                city_ids: [],
                category_ids: {}
            },
            request_id: ""
        }

        try {
            const res = await getSearchSpaceList(searchPayload);


            if (isSetSearchQueryToRoute) {
                setSearchQueryToRoute(queryData)
            }


            trackSearchPerformed({
                filtersHash: filtersHash(queryData.filter_options),
                cityIds: queryData.filter_options.city_ids,
                categoryIds: queryData.filter_options.category_ids,
                people: queryData.filter_options.people_cap_range?.min,
                priceBand: priceBand(queryData.filter_options.price_range?.min),
                resultCount: res.total,
                requestId: res.request_id,
            })
            // BI-UI v2:implicit feature portal_discovery,不帶 feature_id/flow_id
            // （implementation.md §5.1）。useBehaviorFlow 內部已依 capabilities 自行決定
            // 是否真的送出，這裡不需要再判斷 v2 是否啟用。
            behaviorFlow.trackSearchPerformed(res.request_id, res.total)

            if (res.total > 0) {

                searchResultStore.setSpaceList(await enrichAvailability(res.list, capturedDate))
                searchResultStore.setRequestId(res.request_id)
                searchResultStore.setSpaceListTotal(res.total)
                reportImpressions(res.list)
            } else {
                searchResultStore.setSpaceList([])
                searchResultStore.setRequestId('')
                searchResultStore.setSpaceListTotal(0)

                trackSearchNoResult({
                    city: toCsv(queryData.filter_options.city_ids),
                    category: toCsv(Object.values(queryData.filter_options.category_ids || {}).flat()),
                    people: queryData.filter_options.people_cap_range?.min,
                })
                behaviorFlow.trackSearchNoResult(res.request_id)

                // 如果沒有搜尋結果，則推薦相關空間
                // 如果有選擇城市，則推薦相同城市的空間
                // 如果有選擇類別，則推薦相同類別的空間
                if (queryData.filter_options.city_ids.length !== 0) {
                    recommendQueryData.filter_options.city_ids = queryData.filter_options.city_ids
                } else if (queryData.filter_options.category_ids[typeCategoryId]?.length !== 0) {
                    recommendQueryData.filter_options.category_ids[typeCategoryId] = queryData.filter_options.category_ids[typeCategoryId]
                }

                /// 非 geo search 才有推薦空間
                if (searchQueryStore.getGeoSearch.latitude === 0 && searchQueryStore.getGeoSearch.longitude === 0) {
                    const recommendSpaceListRes = await getSearchSpaceList(recommendQueryData)
                    // 推薦空間一樣使用空間列表
                    searchResultStore.setSpaceList(await enrichAvailability(recommendSpaceListRes.list, capturedDate))
                    reportImpressions(recommendSpaceListRes.list)
                }
            }

        } catch (error) {
            console.log(error);
        }

    }


    return {
        handleSearchWithStore
    }
}
